import { Link } from 'react-router-dom';
import { Row, Col, Card, CardBody, CardTitle, CardText, Alert, Badge } from 'reactstrap';
import { PageHeader } from '../../components/PageHeader';

/**
 * Core-Settings-Overview.
 *
 * Zeigt sowohl die existierenden Settings-Bereiche als auch klar die
 * Bereiche, die früher Cards waren aber jetzt entweder anderswo leben
 * (Integrations) oder noch nicht implementiert sind (Tax-Matrix als
 * static, SLA-Timers existieren nicht im Core).
 */
const CARDS = [
  {
    to: '/admin/settings/app',
    icon: '⚙️',
    title: 'App-Settings',
    description: '27 typisierte Settings, gruppiert (Steuern · Sicherheit · Auth-TTLs · Mail · Payment · Identity).',
    status: 'live',
  },
  {
    to: '/admin/integrations',
    icon: '🔌',
    title: 'Integrations',
    description: 'Per-Tenant Provider-Credentials — Stripe, lexoffice, SMTP, PayPal, BZSt, Captcha.',
    status: 'live',
    note: 'Stripe und lexoffice leben hier — kein eigenes Settings-Panel mehr.',
  },
  {
    to: '/admin/settings/core/tax-rates',
    icon: '💶',
    title: 'Tax-Rates (Read-Only)',
    description: 'EU-VAT-Rates aus utils/vatRates.js. Anzeige nur, Änderung via Code-Deploy.',
    status: 'live',
  },
];

const RETIRED = [
  {
    icon: '💳',
    title: 'Stripe (separate Settings)',
    description: 'Per Core-Spec sind Stripe-Tenant-Credentials Teil von Integrations.',
    pointer: '→ /admin/integrations · Card "Stripe"',
  },
  {
    icon: '📒',
    title: 'lexoffice (separate Settings)',
    description: 'Per Core-Spec Teil von Integrations.',
    pointer: '→ /admin/integrations · Card "lexoffice"',
  },
  {
    icon: '⏱️',
    title: 'Global SLA',
    description: 'Per Core-Entscheidung nicht implementiert — vorgeschlagene SLA-Keys haben keine Code-Konsumenten.',
    pointer: 'orbit-specs/done/admin-global-sla-timers.md · Verworfen',
  },
];

export function CoreSettingsOverview() {
  return (
    <div>
      <PageHeader
        title="Core API · Einstellungen"
        subtitle="System-weite Konfiguration. Brand-spezifische Settings unter Brand-Settings pro Brand."
      />

      <Row className="g-3 mb-4">
        {CARDS.map((c) => (
          <Col md="6" lg="6" key={c.to}>
            <Card
              tag={Link}
              to={c.to}
              className="h-100 text-decoration-none text-body"
              style={{ cursor: 'pointer' }}
            >
              <CardBody>
                <CardTitle tag="h5" className="d-flex align-items-center justify-content-between">
                  <span>
                    <span className="me-2" aria-hidden>{c.icon}</span>
                    {c.title}
                  </span>
                  <Badge color="success" pill className="small">live</Badge>
                </CardTitle>
                <CardText className="text-muted small mb-2">{c.description}</CardText>
                {c.note && (
                  <CardText className="small fst-italic text-info-emphasis mb-2">
                    {c.note}
                  </CardText>
                )}
                <span className="text-primary small">Öffnen →</span>
              </CardBody>
            </Card>
          </Col>
        ))}
      </Row>

      <Alert color="secondary" className="small mb-2">
        <strong>Hinweis:</strong> Frühere Settings-Pages für Stripe, lexoffice, Tax-Matrix
        und Global SLA wurden entfernt. Stripe und lexoffice leben jetzt unter Integrations.
        Tax und SLA sind aktuell nicht editierbar — siehe Begründungen unten.
      </Alert>

      <Row className="g-2">
        {RETIRED.map((r) => (
          <Col md="6" key={r.title}>
            <Card className="h-100" style={{ opacity: 0.85 }}>
              <CardBody className="py-3">
                <CardTitle tag="h6" className="mb-1">
                  <span className="me-2" aria-hidden>{r.icon}</span>
                  {r.title}{' '}
                  <Badge color="secondary" pill className="small ms-1">archived</Badge>
                </CardTitle>
                <CardText className="text-muted small mb-1">{r.description}</CardText>
                <CardText className="small mb-0 font-monospace">
                  {r.pointer}
                </CardText>
              </CardBody>
            </Card>
          </Col>
        ))}
      </Row>
    </div>
  );
}
